import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useCreatePartFinderReport } from '@/hooks/api/part-finder-reports/useCreatePartFinderReport';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay, getApiError } from '@/types/apiError';
import { Button, CircularProgress, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartFinderPageObjectModel } from './partFinderPageObjectModel';

const FormContainer = styled('form')({
  maxWidth: '600px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const PartFinderForm = () => {
  const [partNumber, setPartNumber] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<ApiErrorDisplay>();
  const { mutateAsync: createReport, isPending } = useCreatePartFinderReport();
  const { showToast } = useToast();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);

    if (!partNumber.trim()) {
      setError(new Error('Part number is required'));
      return;
    }

    try {
      const response = await createReport({
        partNumber: partNumber.trim(),
        description: description.trim() || undefined,
      });
      showToast({
        message: 'Part finder report created successfully',
        type: 'success',
      });
      navigate(getPath(`part-finder-reports/${response.data.partFinderReport.id}`));
    } catch (error) {
      setError(error as ApiErrorDisplay);
    }
  };

  const apiError = getApiError(error, 'Failed to create part finder report');

  return (
    <FormContainer
      onSubmit={event => {
        handleSubmit(event);
      }}
    >
      <ApiErrorAlert error={apiError} data-testid={PartFinderPageObjectModel.ErrorAlert} />
      <TextField
        label='Part Number'
        value={partNumber}
        onChange={event => setPartNumber(event.target.value)}
        placeholder='e.g., 1234-12-345-6789'
        required
        fullWidth
        slotProps={{ htmlInput: { 'data-testid': PartFinderPageObjectModel.PartNumberInput } }}
      />
      <TextField
        label='Description (Optional)'
        value={description}
        onChange={event => setDescription(event.target.value)}
        placeholder='e.g., Hydraulic pump assembly'
        multiline
        rows={3}
        fullWidth
        slotProps={{ htmlInput: { 'data-testid': PartFinderPageObjectModel.DescriptionInput } }}
      />
      <Button
        type='submit'
        variant='contained'
        color='primary'
        disabled={isPending || !partNumber.trim()}
        data-testid={PartFinderPageObjectModel.FindPartButton}
        sx={{
          textTransform: 'none',
          borderRadius: '6px',
          padding: '12px 0',
          width: '100px',
          alignSelf: 'flex-start',
        }}
      >
        {isPending ? (
          <CircularProgress size={24} data-testid={PartFinderPageObjectModel.LoadingSpinner} />
        ) : (
          'Find Part'
        )}
      </Button>
    </FormContainer>
  );
};
